import React, { useMemo } from 'react';

import { Alert, Form, type FormRule, Modal, Tabs } from 'antd-v5';
import { MdInfo } from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { Button, Input, Select, Text } from 'scouting-ui/lib';
import { cssVars } from 'scouting-ui/lib/variables';

import type { SubUnitType } from '@appTypes/esb';
import {
  type PendingLevelChanges,
  useSubUnitEditor,
} from '@modules/roster-v2/hooks/useSubUnitAdmin';
import {
  DEN_LEVEL_OPTIONS,
  type RosterMemberView,
} from '@modules/roster-v2/models/rosterMemberView';
import type * as SubUnitAdmin from '@modules/roster-v2/models/subUnitAdmin';
import { validateSubUnitName } from '@modules/roster-v2/models/subUnitAdmin';
import type { SubUnitSaveStep } from '@modules/roster/subUnitMutations';
import { intl } from '@shared/localization';

import MemberTransfer, { type MemberPickerProps } from './MemberTransfer';
import styles from './SubUnitModal.module.scss';

const GUIDE_TO_ADVANCEMENT_URL =
  'https://www.scouting.org/resources/guide-to-advancement/';

enum FormField {
  Name = 'name',
  DenType = 'denType',
  YouthIds = 'youthIds',
  AdultIds = 'adultIds',
  DenChiefIds = 'denChiefIds',
}

function nameErrorMessage(
  error: SubUnitAdmin.SubUnitNameError,
  subUnitType: SubUnitType,
  name: string,
): string {
  switch (error) {
    case 'required':
      return intl.formatMessage({ id: 'shared.form.error.isRequired' });
    case 'invalidCharacters':
      return intl.formatMessage({
        id: 'shared.address.address1.errors.specialCharsValidation',
      });
    case 'mustStartWithDigit':
      return intl.formatMessage({
        id: 'rosterV2.subUnits.form.name.mustStartWithDigit',
      });
    case 'taken':
    default:
      return intl.formatMessage(
        { id: `rosterV2.subUnits.form.name.taken.${subUnitType}` },
        { name: name.trim() },
      );
  }
}

const EMPTY_IDS: number[] = [];
const EMPTY_IMAGES: Partial<Record<string, string>> = {};
const noop = () => {};

export interface SubUnitModalViewProps {
  open: boolean;
  mode: SubUnitAdmin.SubUnitFormMode;
  subUnitType: SubUnitType;
  /** Applied on each opening; leave out while loading to show a spinner */
  initialValues?: SubUnitAdmin.SubUnitValues;
  youthOptions: SubUnitAdmin.SubUnitMemberOption[];
  adultOptions: SubUnitAdmin.SubUnitMemberOption[];
  /** Dens only; leave out to hide the Den Chiefs tab (loading or failed) */
  denChiefOptions?: SubUnitAdmin.SubUnitMemberOption[];
  /** Rank image URL per den level (`denTypesNames` value) */
  denLevelImages?: Partial<Record<string, string>>;
  /** Other sub-units' names, for the unique-name rule */
  takenNames: string[];
  saving: boolean;
  /** Server data Save depends on is loading, refetching or failed */
  saveDisabled?: boolean;
  partialFailure?: SubUnitSaveStep[];
  /** An error with Retry: instead of the form before it opens, else above it */
  loadFailed?: boolean;
  onRetryLoad?: () => void;
  onSubmit: (values: SubUnitAdmin.SubUnitValues) => void;
  /** Edit mode, once nobody is left */
  onDelete?: () => void;
  onCancel: () => void;
  afterClose?: () => void;
}

type SubUnitFormProps = SubUnitModalViewProps & {
  initialValues: SubUnitAdmin.SubUnitValues;
};

type MemberFieldProps = Omit<MemberPickerProps, 'value' | 'onChange'> &
  Partial<Pick<MemberPickerProps, 'value' | 'onChange'>>;

const LoadFailedAlert: React.FC<
  Pick<SubUnitModalViewProps, 'subUnitType' | 'onRetryLoad'>
> = props => {
  const { subUnitType, onRetryLoad } = props;

  return (
    <Alert
      type="error"
      showIcon
      message={
        <FormattedMessage
          id={`rosterV2.subUnits.modal.loadFailed.${subUnitType}`}
        />
      }
      action={
        onRetryLoad && (
          <Button size="small" type="default" onClick={onRetryLoad}>
            <FormattedMessage id="rosterV2.subUnits.modal.loadFailed.retry" />
          </Button>
        )
      }
    />
  );
};

/** `Form.Item` injects `value` and `onChange` */
const MemberField: React.FC<MemberFieldProps> = props => {
  const { value = EMPTY_IDS, onChange = noop, ...pickerProps } = props;

  return <MemberTransfer {...pickerProps} value={value} onChange={onChange} />;
};

/** Inside the Modal, so `destroyOnClose` gives each opening a fresh form */
const SubUnitForm: React.FC<SubUnitFormProps> = props => {
  const {
    mode,
    subUnitType,
    initialValues,
    youthOptions,
    adultOptions,
    denChiefOptions,
    denLevelImages = EMPTY_IMAGES,
    takenNames,
    saving,
    saveDisabled = false,
    partialFailure = [],
    loadFailed = false,
    onRetryLoad,
    onSubmit,
    onDelete,
    onCancel,
  } = props;

  const [form] = Form.useForm<SubUnitAdmin.SubUnitValues>();
  const isDen = subUnitType === 'den';
  const youthIds: number[] =
    Form.useWatch(FormField.YouthIds, form) ?? initialValues.youthIds;
  const adultIds: number[] =
    Form.useWatch(FormField.AdultIds, form) ?? initialValues.adultIds;
  const denChiefIds: number[] =
    Form.useWatch(FormField.DenChiefIds, form) ?? initialValues.denChiefIds;
  const showDenChiefs = isDen && !!denChiefOptions;

  const hasMembers =
    youthIds.length > 0 ||
    adultIds.length > 0 ||
    (showDenChiefs && denChiefIds.length > 0);
  const showDelete = mode === 'edit' && !hasMembers && !!onDelete;

  // Edit: a stored name that predates the rules mustn't block saving members
  const storedName = mode === 'edit' ? initialValues.name : undefined;
  const nameRules = useMemo<FormRule[]>(
    () => [
      ({ getFieldValue }) => ({
        required: true,
        validator: async (_, value?: string) => {
          const error = validateSubUnitName({
            name: value,
            subUnitType,
            denType: getFieldValue(FormField.DenType),
            takenNames,
            storedName,
          });
          if (error) {
            throw new Error(nameErrorMessage(error, subUnitType, value ?? ''));
          }
        },
      }),
    ],
    [takenNames, subUnitType, storedName],
  );

  const denLevelOptions = useMemo(
    () =>
      DEN_LEVEL_OPTIONS.map(({ value, label }) => {
        const imageUrl = denLevelImages[value];
        return {
          value,
          label: (
            <span className={styles.denLevelOption}>
              {imageUrl && (
                <img src={imageUrl} alt="" className={styles.rankImage} />
              )}
              {label}
            </span>
          ),
        };
      }),
    [denLevelImages],
  );

  const handleValuesChange = (changed: Partial<SubUnitAdmin.SubUnitValues>) => {
    // The digit rule depends on the level, so re-check a typed name
    if (FormField.DenType in changed && form.getFieldValue(FormField.Name)) {
      form.validateFields([FormField.Name]).catch(noop);
    }
  };

  // `values` has registered fields only; Den Chiefs only exists with its tab
  const handleFinish = (values: SubUnitAdmin.SubUnitValues) => {
    onSubmit({
      name: values.name.trim(),
      denType: isDen ? values.denType : undefined,
      youthIds: values.youthIds ?? [],
      adultIds: values.adultIds ?? [],
      denChiefIds: showDenChiefs ? values.denChiefIds ?? [] : [],
    });
  };

  const pickerProps = {
    subUnitType,
    disabled: saving,
  };
  const youthField = (
    <Form.Item name={FormField.YouthIds} noStyle>
      <MemberField {...pickerProps} group="youths" options={youthOptions} />
    </Form.Item>
  );
  const adultField = (
    <Form.Item name={FormField.AdultIds} noStyle>
      <MemberField {...pickerProps} group="adults" options={adultOptions} />
    </Form.Item>
  );
  const denChiefField = isDen && denChiefOptions && (
    <Form.Item name={FormField.DenChiefIds} noStyle>
      <MemberField
        {...pickerProps}
        group="denChiefs"
        options={denChiefOptions}
      />
    </Form.Item>
  );

  let footerHintId: string | undefined;
  if (showDelete) {
    footerHintId = `rosterV2.subUnits.form.hint.empty.${subUnitType}`;
  } else if (!hasMembers) {
    footerHintId = 'rosterV2.subUnits.form.hint.noMembers';
  } else if (saveDisabled && !loadFailed) {
    footerHintId = 'rosterV2.subUnits.form.hint.refreshing';
  }

  return (
    <Form
      form={form}
      // Prefixes field ids so browsers don't autofill a person's name
      name="subUnitForm"
      autoComplete="off"
      initialValues={initialValues}
      onValuesChange={handleValuesChange}
      onFinish={handleFinish}
      disabled={saving}
      className={styles.form}
    >
      {loadFailed && (
        <LoadFailedAlert subUnitType={subUnitType} onRetryLoad={onRetryLoad} />
      )}
      {partialFailure.length > 0 && (
        <Alert
          type="warning"
          showIcon
          message={
            <FormattedMessage
              id={`rosterV2.subUnits.form.partialFailure.title.${subUnitType}`}
            />
          }
          description={
            <div className={styles.failure}>
              <Text>
                <FormattedMessage id="rosterV2.subUnits.form.partialFailure.steps" />
              </Text>
              <ul className={styles.failureSteps}>
                {partialFailure.map(step => (
                  <li key={step}>
                    <FormattedMessage
                      id={
                        step === 'rename'
                          ? `rosterV2.subUnits.form.partialFailure.step.rename.${subUnitType}`
                          : `rosterV2.subUnits.form.partialFailure.step.${step}`
                      }
                    />
                  </li>
                ))}
              </ul>
              <Text>
                <FormattedMessage id="rosterV2.subUnits.form.partialFailure.retry" />
              </Text>
            </div>
          }
        />
      )}

      <div className={styles.fields}>
        <Form.Item
          name={FormField.Name}
          rules={nameRules}
          className={styles.nameField}
        >
          <Input
            autoComplete="off"
            placeholder={intl.formatMessage({
              id: `rosterV2.subUnits.form.name.${subUnitType}`,
            })}
          />
        </Form.Item>
        {isDen && (
          <Form.Item
            name={FormField.DenType}
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: 'shared.form.error.isRequired',
                }),
              },
            ]}
            className={styles.levelField}
          >
            <Select
              minWidth={0}
              placeholder={intl.formatMessage({
                id: 'rosterV2.subUnits.form.denLevel',
              })}
              options={denLevelOptions}
            />
          </Form.Item>
        )}
      </div>

      {isDen && (
        <div className={styles.note}>
          <MdInfo className={styles.noteIcon} color={cssVars['color-info']} />
          <Text font="body-caption">
            <FormattedMessage
              id="rosterV2.subUnits.form.denLevelNote"
              values={{
                guideLink: (
                  <a
                    href={GUIDE_TO_ADVANCEMENT_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FormattedMessage id="rosterV2.subUnits.form.denLevelNote.link" />
                  </a>
                ),
              }}
            />
          </Text>
        </div>
      )}

      <Tabs
        items={[
          {
            key: FormField.YouthIds,
            label: (
              <FormattedMessage
                id="rosterV2.subUnits.picker.tab.youths"
                values={{ count: youthIds.length }}
              />
            ),
            forceRender: true,
            children: youthField,
          },
          {
            key: FormField.AdultIds,
            label: (
              <FormattedMessage
                id="rosterV2.subUnits.picker.tab.adults"
                values={{ count: adultIds.length }}
              />
            ),
            forceRender: true,
            children: adultField,
          },
          ...(showDenChiefs
            ? [
                {
                  key: FormField.DenChiefIds,
                  label: (
                    <FormattedMessage
                      id="rosterV2.subUnits.picker.tab.denChiefs"
                      values={{ count: denChiefIds.length }}
                    />
                  ),
                  forceRender: true,
                  children: denChiefField,
                },
              ]
            : []),
        ]}
      />

      <div className={styles.footer}>
        {footerHintId && (
          <Text font="body-caption" type="secondary" className={styles.hint}>
            <FormattedMessage id={footerHintId} />
          </Text>
        )}
        <div className={styles.actions}>
          <Button type="default" disabled={saving} onClick={onCancel}>
            <FormattedMessage id="shared.cancel" />
          </Button>
          {showDelete ? (
            <Button danger disabled={saveDisabled} onClick={onDelete}>
              <FormattedMessage
                id={`rosterV2.subUnits.form.delete.${subUnitType}`}
              />
            </Button>
          ) : (
            <Button
              htmlType="submit"
              loading={saving}
              disabled={!hasMembers || saveDisabled}
            >
              <FormattedMessage id="shared.save" />
            </Button>
          )}
        </div>
      </div>
    </Form>
  );
};

/** Desktop only: the modal is full width on mobile */
const MODAL_WIDTH = 800;

/** One Modal for spinner, load error and form, so it animates in once. */
export const SubUnitModalView: React.FC<SubUnitModalViewProps> = props => {
  const {
    open,
    mode,
    subUnitType,
    initialValues,
    saving,
    loadFailed = false,
    onRetryLoad,
    onCancel,
    afterClose,
  } = props;

  let content: React.ReactNode = null;
  if (initialValues) {
    content = <SubUnitForm {...props} initialValues={initialValues} />;
  } else if (loadFailed) {
    content = (
      <LoadFailedAlert subUnitType={subUnitType} onRetryLoad={onRetryLoad} />
    );
  }

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      afterClose={afterClose}
      footer={null}
      width={MODAL_WIDTH}
      destroyOnClose
      loading={!content}
      maskClosable={false}
      closable={!saving}
      keyboard={!saving}
      className={styles.modal}
      wrapClassName={styles.wrap}
      title={
        <FormattedMessage
          id={`rosterV2.subUnits.modal.title.${mode}.${subUnitType}`}
        />
      }
    >
      {content}
    </Modal>
  );
};

interface SubUnitModalContainerProps {
  open: boolean;
  orgGuid: string;
  subUnitType: SubUnitType;
  members: readonly RosterMemberView[];
  /** Leave out to add one */
  subUnitId?: number;
  /** From the page, so it outlives each opening */
  pendingLevelChanges: PendingLevelChanges;
  onClose: () => void;
  /** Close animation ended: safe to unmount */
  afterClose?: () => void;
  /** Edit mode, once nobody is left */
  onDelete: (subUnitId: number) => void;
}

/**
 * Mount one per opening (new `key`) so its session state starts fresh.
 * Unmount in `afterClose`: while mounted it rebuilds on `members` changes.
 */
const SubUnitModalContainer: React.FC<SubUnitModalContainerProps> = props => {
  const {
    open,
    orgGuid,
    subUnitType,
    members,
    subUnitId,
    pendingLevelChanges,
    onClose,
    afterClose,
    onDelete,
  } = props;

  const editor = useSubUnitEditor({
    open,
    orgGuid,
    subUnitType,
    members,
    initialSubUnitId: subUnitId,
    pendingLevelChanges,
    onClose,
  });
  const editingId = editor.subUnitId;

  return (
    <SubUnitModalView
      {...editor.viewProps}
      open={open}
      onCancel={onClose}
      afterClose={afterClose}
      onDelete={editingId === undefined ? undefined : () => onDelete(editingId)}
    />
  );
};

export default SubUnitModalContainer;
